import React from 'react';

import { cachedSiteLanguages, fetchSiteLanguages, type SiteLanguages } from '../ad-language-api';

/** The brand website's languages as a composer's Language section reads them. `error`: the read failed (nothing is
 *  known; a later mount reads again). */
export interface SiteLanguagesState extends SiteLanguages {
  status: 'loading' | 'ready' | 'error';
}

const LOADING: SiteLanguagesState = { status: 'loading', languages: [], defaultLanguage: '', known: false };
const FAILED: SiteLanguagesState = { status: 'error', languages: [], defaultLanguage: '', known: false };

/** One state object per answer, so a consumer's memo or effect keyed on it does not re-run on every render. */
const readyStates = new WeakMap<SiteLanguages, SiteLanguagesState>();
const readyState = (site: SiteLanguages): SiteLanguagesState => {
  const known = readyStates.get(site);
  if (known) return known;
  const state: SiteLanguagesState = { status: 'ready', ...site };
  readyStates.set(site, state);
  return state;
};

/** The brand website's languages (the Language picker's options, the default first). Read once per tenant and shared
 *  by every composer; `loading` while there is no tenant. */
export const useSiteLanguages = (tenantId: number | undefined): SiteLanguagesState => {
  // Keyed by tenant, so a composer that outlives a brand switch never shows the other brand's languages.
  const [read, setRead] = React.useState<{ tenantId: number; state: SiteLanguagesState } | null>(() => {
    const cached = tenantId ? cachedSiteLanguages(tenantId) : undefined;
    return tenantId && cached ? { tenantId, state: readyState(cached) } : null;
  });

  // Syncs with the network: reads the tenant's website languages (cached per tenant by fetchSiteLanguages).
  React.useEffect(() => {
    if (!tenantId) return undefined;
    let live = true;
    void fetchSiteLanguages(tenantId).then((site) => {
      if (live) setRead({ tenantId, state: site ? readyState(site) : FAILED });
    });
    return () => {
      live = false;
    };
  }, [tenantId]);

  if (!tenantId) return LOADING;
  if (read?.tenantId === tenantId) return read.state;
  const cached = cachedSiteLanguages(tenantId);
  return cached ? readyState(cached) : LOADING;
};

export default useSiteLanguages;
